// src/components/mdx/client/visualizations/PieVsBarComparisonChart.client.tsx

/**
 * Side-by-side comparison of pie vs horizontal bar for channel mix data.
 */

'use client'

import { useMemo } from 'react'

import { Bar, BarChart, CartesianGrid, Cell, LabelList, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from '~/lib/charts/client'
import type { NameType, TooltipContentType, ValueType } from '~/lib/charts/types'
import { getOrCreateTooltipWrapper, type CustomTooltipProps } from '~/lib/charts/tooltip'
import { AXIS_TOKENS, MARKETING_DATA_VIZ_SERIES } from '~/components/mdx/client/visualizations/blogChartPalette'
import { BlogChartShell } from '~/components/mdx/client/visualizations/BlogChartShell.client'
import { BLOG_CHART_THROTTLE_MS, BlogChartPlotArea, BlogChartTooltipShell, BLOG_CHART_LABEL_STYLE, formatBarEndPercent } from '~/components/mdx/client/visualizations/blogChartLayout'
import { ChartVisibilityBoundary } from '~/components/mdx/client/visualizations/ChartVisibilityBoundary.client'
import { generateChannelMixData } from '~/components/mdx/client/visualizations/marketingDataVizData'
import { useMediaQuery } from '~/lib/hooks/useMediaQuery'

const COLORS = [
  MARKETING_DATA_VIZ_SERIES.primary,
  MARKETING_DATA_VIZ_SERIES.metaRoas,
  MARKETING_DATA_VIZ_SERIES.secondary,
  MARKETING_DATA_VIZ_SERIES.retention,
  MARKETING_DATA_VIZ_SERIES.mer,
  MARKETING_DATA_VIZ_SERIES.benchmark
]

function MixTooltip({ active, payload }: CustomTooltipProps): JSX.Element | null {
  if (!active || !payload?.length) {
    return null
  }
  return (
    <BlogChartTooltipShell>
      <p className="text-gray-200">
        {payload[0]?.name}: <span className="font-semibold text-white">{payload[0]?.value}%</span>
      </p>
    </BlogChartTooltipShell>
  )
}

interface PieVsBarComparisonChartContentProps {
  isActive: boolean
}

function PieVsBarComparisonChartContent({ isActive }: PieVsBarComparisonChartContentProps): JSX.Element {
  const data = useMemo(() => generateChannelMixData(), [])
  const isMobile = useMediaQuery('(max-width: 768px)')
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
  const tooltip = useMemo(() => getOrCreateTooltipWrapper(MixTooltip), [])

  return (
    <BlogChartShell
      title="Pie vs Bar: Channel Mix"
      description="Humans compare bar lengths more accurately than pie angles — especially beyond 3–4 slices."
      gradient="from-amber-400 to-rose-400"
    >
      <div className={`grid gap-6 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
        <div className="flex min-w-0 flex-col">
          <p className="mb-2 text-center text-sm font-medium text-rose-300">Pie chart (6 slices)</p>
          <BlogChartPlotArea className="h-[220px] sm:h-[280px]">
            <ResponsiveContainer width="100%" height="100%" debounce={50} initialDimension={{ width: 360, height: 320 }}>
              <PieChart accessibilityLayer throttleDelay={BLOG_CHART_THROTTLE_MS}>
                <Tooltip content={tooltip as TooltipContentType<ValueType, NameType>} />
                <Pie
                  data={data}
                  dataKey="share"
                  nameKey="channel"
                  cx="50%"
                  cy="50%"
                  innerRadius="36%"
                  outerRadius="64%"
                  label={false}
                  labelLine={false}
                  animationDuration={isActive && !prefersReducedMotion ? 600 : 0}
                >
                  {data.map((entry, index) => (
                    <Cell key={entry.channel} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </BlogChartPlotArea>
          <p className="mt-2 text-center text-xs text-gray-500">No slice labels: the point is that the sorted bars are easier to read.</p>
        </div>

        <div className="flex min-w-0 flex-col">
          <p className="mb-2 text-center text-sm font-medium text-emerald-300">Sorted horizontal bar</p>
          <BlogChartPlotArea className="h-[280px] sm:h-[320px]">
            <ResponsiveContainer width="100%" height="100%" debounce={50} initialDimension={{ width: 360, height: 320 }}>
              <BarChart data={data} layout="vertical" margin={{ top: 8, right: 40, left: 8, bottom: 8 }} accessibilityLayer throttleDelay={BLOG_CHART_THROTTLE_MS}>
                <CartesianGrid stroke={AXIS_TOKENS.grid} strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" stroke={AXIS_TOKENS.stroke} fontSize={10} domain={[0, 40]} tickFormatter={value => `${value}%`} />
                <YAxis type="category" dataKey="channel" stroke={AXIS_TOKENS.stroke} fontSize={10} width={72} />
                <Tooltip content={tooltip as TooltipContentType<ValueType, NameType>} />
                <Bar dataKey="share" name="Share" radius={[0, 4, 4, 0]} animationDuration={isActive && !prefersReducedMotion ? 600 : 0}>
                  <LabelList
                    dataKey="share"
                    position="right"
                    formatter={formatBarEndPercent}
                    {...BLOG_CHART_LABEL_STYLE}
                  />
                  {data.map((entry, index) => (
                    <Cell key={entry.channel} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </BlogChartPlotArea>
        </div>
      </div>

    </BlogChartShell>
  )
}

export default function PieVsBarComparisonChart(): JSX.Element {
  return (
    <ChartVisibilityBoundary aria-label="Pie chart versus horizontal bar comparison">
      {({ isIntersecting }) => (
        <PieVsBarComparisonChartContent isActive={isIntersecting} />
      )}
    </ChartVisibilityBoundary>
  )
}
